import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Save, KeyRound, CheckCircle2, Bell, Volume2 } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { useNotificationsStore } from '../../lib/notifications-store';
import { notify } from '../../lib/notify';
import {
  changePasswordSchema,
  updateProfileSchema,
  type ChangePasswordInput,
  type UpdateProfileInput,
} from '../../lib/schemas';
import { Modal } from '../../components/Modal';
import type { User } from '@vipsoft/shared-types';

type Me = Pick<User, 'id' | 'email' | 'name' | 'role'> & {
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
};

export default function Profile() {
  const setSession = useAuthStore((s) => s.setSession);
  const accessToken = useAuthStore((s) => s.accessToken);
  const storeUser = useAuthStore((s) => s.user);

  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [pwOpen, setPwOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    setLoading(true);
    apiFetch<Me>('/me')
      .then((data) => {
        setMe(data);
        reset({ name: data.name });
      })
      .catch((err: Error) => notify.error('No pudimos cargar tu perfil.', err.message))
      .finally(() => setLoading(false));
  }, [reset]);

  const onSave = async (values: UpdateProfileInput) => {
    if (!me) return;
    try {
      const updated = await apiFetch<Me>('/me', {
        method: 'PATCH',
        body: { name: values.name },
      });
      setMe(updated);
      reset({ name: updated.name });
      if (accessToken && storeUser) {
        setSession(accessToken, { ...storeUser, name: updated.name });
      }
      notify.success('Perfil actualizado.');
    } catch (err) {
      notify.apiError(err, 'No se pudo guardar el perfil.');
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin" />
        Cargando perfil…
      </div>
    );
  }

  if (!me) return null;

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-1">Información de la cuenta</h2>
        <p className="text-sm text-slate-500 mb-5">
          Tu nombre se muestra al asignar tickets y en notas internas.
        </p>

        <form onSubmit={handleSubmit(onSave)} className="space-y-4" noValidate>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nombre</label>
            <input
              type="text"
              autoComplete="name"
              aria-invalid={errors.name ? 'true' : 'false'}
              {...register('name')}
              className={`w-full max-w-sm px-3 py-2 text-sm rounded-lg border bg-white focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-rose-300 focus:ring-rose-500'
                  : 'border-slate-200 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
            {errors.name && (
              <p className="text-xs text-rose-600 mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Correo electrónico</label>
            <input
              type="email"
              value={me.email}
              readOnly
              className="w-full max-w-sm px-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              El correo solo puede cambiarlo un administrador.
            </p>
          </div>

          <div className="flex items-center gap-4 pt-1 text-xs text-slate-500">
            <span>
              Rol: <strong className="text-slate-700">{roleLabel(me.role)}</strong>
            </span>
            {me.last_login_at && (
              <span>
                Última sesión:{' '}
                <strong className="text-slate-700">{formatDate(me.last_login_at)}</strong>
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 pt-5 border-t border-slate-100">
            <button
              type="submit"
              disabled={!isDirty || isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar cambios
            </button>
          </div>
        </form>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-1">Notificaciones</h2>
        <p className="text-sm text-slate-500 mb-5">
          Cómo te avisamos cuando entran tickets nuevos al sistema.
        </p>
        <NotificationsPrefs />
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-1">Seguridad</h2>
        <p className="text-sm text-slate-500 mb-5">
          Cambia tu contraseña periódicamente. Necesitas conocer la actual.
        </p>
        <button
          onClick={() => setPwOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        >
          <KeyRound size={14} />
          Cambiar contraseña
        </button>
      </section>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}

function roleLabel(role: string) {
  return (
    ({ admin: 'Administrador', supervisor: 'Supervisor', agent: 'Agente', customer: 'Cliente' } as Record<
      string,
      string
    >)[role] ?? role
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('es-VE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const onSubmit = async (values: ChangePasswordInput) => {
    try {
      await apiFetch('/auth/change-password', {
        method: 'POST',
        body: { current_password: values.current_password, new_password: values.new_password },
      });
      notify.success('Contraseña actualizada.', 'Usá la nueva la próxima vez que ingreses.');
      reset();
      onClose();
    } catch (err) {
      notify.apiError(err, 'No se pudo cambiar la contraseña.');
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Cambiar contraseña" size="sm">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" noValidate>
        <Field
          label="Contraseña actual"
          error={errors.current_password?.message}
          input={
            <input
              type="password"
              autoComplete="current-password"
              {...register('current_password')}
              className={inputClass(!!errors.current_password)}
            />
          }
        />
        <Field
          label="Nueva contraseña"
          hint="Mínimo 8 caracteres."
          error={errors.new_password?.message}
          input={
            <input
              type="password"
              autoComplete="new-password"
              {...register('new_password')}
              className={inputClass(!!errors.new_password)}
            />
          }
        />
        <Field
          label="Confirmar nueva contraseña"
          error={errors.confirm_password?.message}
          input={
            <input
              type="password"
              autoComplete="new-password"
              {...register('confirm_password')}
              className={inputClass(!!errors.confirm_password)}
            />
          }
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
          >
            {isSubmitting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <CheckCircle2 size={14} />
            )}
            Cambiar contraseña
          </button>
        </div>
      </form>
    </Modal>
  );
}

function inputClass(hasError: boolean) {
  return `w-full px-3 py-2 text-sm rounded-lg border bg-white focus:outline-none focus:ring-2 ${
    hasError ? 'border-rose-300 focus:ring-rose-500' : 'border-slate-200 focus:ring-blue-500'
  }`;
}

function Field({
  label,
  hint,
  error,
  input,
}: {
  label: string;
  hint?: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {input}
      {error ? (
        <p className="text-xs text-rose-600 mt-1">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-slate-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

function NotificationsPrefs() {
  const soundEnabled = useNotificationsStore((s) => s.soundEnabled);
  const browserNotifEnabled = useNotificationsStore((s) => s.browserNotifEnabled);
  const setSoundEnabled = useNotificationsStore((s) => s.setSoundEnabled);
  const setBrowserNotifEnabled = useNotificationsStore((s) => s.setBrowserNotifEnabled);

  // Sincronizamos browserNotifEnabled con el estado real del permission.
  // Si el usuario lo negó a nivel del browser, no sirve tenerlo en true en
  // nuestro store.
  const browserPerm =
    typeof Notification !== 'undefined' ? Notification.permission : 'denied';

  const toggleBrowser = async () => {
    if (!browserNotifEnabled) {
      if (typeof Notification === 'undefined') {
        notify.error('Tu navegador no soporta notificaciones nativas.');
        return;
      }
      if (browserPerm === 'denied') {
        notify.error(
          'Notificaciones bloqueadas.',
          'Habilítalas desde la configuración del navegador para este sitio.',
        );
        return;
      }
      const result = await Notification.requestPermission();
      if (result !== 'granted') {
        notify.error('Permiso denegado.', 'Si cambias de opinión, vuelve a habilitarlas aquí.');
        return;
      }
      setBrowserNotifEnabled(true);
      notify.success('Notificaciones del navegador activadas.');
    } else {
      setBrowserNotifEnabled(false);
    }
  };

  return (
    <div className="space-y-3">
      <PrefRow
        icon={<Volume2 size={18} />}
        title="Sonido al recibir un ticket nuevo"
        description="Un beep corto cuando entra un ticket. Solo se escucha si el navegador permite reproducir audio (típicamente después del primer clic en la página)."
        enabled={soundEnabled}
        onToggle={() => setSoundEnabled(!soundEnabled)}
      />
      <PrefRow
        icon={<Bell size={18} />}
        title="Notificaciones del sistema operativo"
        description={
          browserPerm === 'denied'
            ? 'Bloqueado por el navegador. Cambialo desde la configuración del sitio.'
            : 'Recibe notificaciones nativas del SO incluso cuando la pestaña no está visible. Pediremos permiso la primera vez.'
        }
        enabled={browserNotifEnabled && browserPerm === 'granted'}
        onToggle={toggleBrowser}
        disabled={browserPerm === 'denied'}
      />
    </div>
  );
}

function PrefRow({
  icon,
  title,
  description,
  enabled,
  onToggle,
  disabled = false,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
      <div className="text-slate-500 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{description}</div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
          disabled
            ? 'bg-slate-200 cursor-not-allowed'
            : enabled
              ? 'bg-emerald-600'
              : 'bg-slate-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
